// import utility functions
import {
  calculateNumeratorDenominatorPayment,
  calculatePrincipalRateTerm,
  deformatNumber,
  formatCurrencyString,
} from '../util';

/**
 * Calculate old (current/original) loan values
 *
 * Parameters are all values from inputs
 *
 * @param {string} originalInterestRate
 * @param {string} originalLoanAmount
 * @param {string} originalLoanTerm
 * @param {string} timeLeftToPayOnOriginalLoan
 * @return {Object} oldValues - properties for monthly loan payment, remaining loan balance, and remaining total cost (loan balnace plus interest payments)
 */
function calculateOldValues(
  originalInterestRate,
  originalLoanAmount,
  originalLoanTerm,
  timeLeftToPayOnOriginalLoan
) {
  // remove the " Months" part of the string
  const monthsLeft = deformatNumber(timeLeftToPayOnOriginalLoan);

  const [
    originalPrincipal,
    originalRate,
    originalTerm,
  ] = calculatePrincipalRateTerm({
    totalHomeLoanAmount: originalLoanAmount,
    annualInterestRate: originalInterestRate,
    termOfTheLoan: originalLoanTerm,
  });

  // don't need numerator or denominator here
  const [, , originalPayment] = calculateNumeratorDenominatorPayment({
    rate: originalRate,
    term: originalTerm,
    principal: originalPrincipal,
  });

  // months of initial loan term minus months remaining to pay
  const monthsPaid = originalTerm - monthsLeft;

  let remainingBalance = originalPrincipal;

  // what's the balance after the months to have been paid have been paid?
  for (let i = 0; i < monthsPaid; i++) {
    remainingBalance = remainingBalance * (1 + originalRate) - originalPayment;
  }

  const oldValues = {
    monthlyPayment: formatCurrencyString(originalPayment),
    remainingBalance: formatCurrencyString(remainingBalance),
    remainingTotal: formatCurrencyString(originalPayment * monthsLeft),
  };

  return oldValues;
}

/**
 * Calculate new loan values
 *
 * Parameters are all values from inputs
 *
 * @param {string} newInterestRate
 * @param {string} newLoanAmount
 * @param {string} newLoanTerm
 * @return {Object} newValues - monthly loan payment and remaining total (balance plus interest) cost
 */
function calculateNewValues(newInterestRate, newLoanAmount, newLoanTerm) {
  const [newPrincipal, newRate, newTerm] = calculatePrincipalRateTerm({
    totalHomeLoanAmount: newLoanAmount,
    annualInterestRate: newInterestRate,
    termOfTheLoan: newLoanTerm,
  });

  // don't need numerator or denominator here
  const [, , newPayment] = calculateNumeratorDenominatorPayment({
    rate: newRate,
    term: newTerm,
    principal: newPrincipal,
  });

  const newValues = {
    monthlyPayment: formatCurrencyString(newPayment),
    totalCost: formatCurrencyString(newPayment * newLoanTerm * 12),
  };

  return newValues;
}

/**
 * Calculate savings (or not) for refinancing loan at different amount, interest rate, and term
 *
 * Formula for this was adapated from the old PHP calculators OTHL sites used to use
 *
 * Parameters are all values from inputs
 *
 * @param {string} originalInterestRate
 * @param {string} originalLoanAmount
 * @param {string} originalLoanTerm
 * @param {string} timeLeftToPayOnOriginalLoan
 * @param {string} newInterestRate
 * @param {string} newLoanAmount
 * @param {string} newLoanTerm
 * @return {[object, object, string]} - array of calculated old values, new values, and total savings with refinancing
 */
function calculateRefinanceSavings(
  originalInterestRate,
  originalLoanAmount,
  originalLoanTerm,
  timeLeftToPayOnOriginalLoan,
  newInterestRate,
  newLoanAmount,
  newLoanTerm
) {
  const oldValues = calculateOldValues(
    originalInterestRate,
    originalLoanAmount,
    originalLoanTerm,
    timeLeftToPayOnOriginalLoan
  );

  const newValues = calculateNewValues(
    newInterestRate,
    newLoanAmount,
    newLoanTerm
  );

  /**
   * maybe not the best way to get this number but is good enough lolz
   * allows keeping oldValues and newValues modularized into their own functions
   */
  newValues.monthlySavings = formatCurrencyString(
    Math.abs(
      parseFloat(newValues.monthlyPayment.replace(/,/g, '')) -
        parseFloat(oldValues.monthlyPayment.replace(/,/g, ''))
    )
  );

  // ditto here
  const totalSavings = formatCurrencyString(
    parseFloat(oldValues.remainingTotal.replace(/,/g, '')) -
      parseFloat(newValues.totalCost.replace(/,/g, ''))
  );

  return [oldValues, newValues, totalSavings];
}

export default calculateRefinanceSavings;



// WEBPACK FOOTER //
// ./scripts/svelte/calculators/refinanceSavingsCalculator/calculateRefinanceSavings.js